import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Hotel, UtensilsCrossed, ConciergeBell, Store } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Tag } from "@/components/Reveal";
import { IMG } from "@/data/images";

const SECTORS = [
  {
    id: "hotels",
    icon: Hotel,
    title: "Hotels & Hospitality",
    image: IMG.fineDining,
    alt: "Fine dining dish prepared with fresh ingredients",
    text: "Buffets, banquets and around-the-clock room service demand produce that holds its peak. Fresh Guard is designed to extend prep windows and cut silent waste in hotel kitchens.",
  },
  {
    id: "restaurants",
    icon: UtensilsCrossed,
    title: "Restaurants",
    image: IMG.restaurantKitchen,
    alt: "Professional restaurant kitchen in service",
    text: "Tight margins leave no room for wilted herbs and tired greens. Longer-lasting produce means fewer emergency orders, more consistent plates and calmer service.",
  },
  {
    id: "catering",
    icon: ConciergeBell,
    title: "Catering Companies",
    image: IMG.chefPlating,
    alt: "Chef plating dishes for a catered event",
    text: "Events are won or lost on timing. Produce designed to stay fresh through transport, holding and service is intended to give caterers a buffer that today's supply chain doesn't.",
  },
  {
    id: "retail",
    icon: Store,
    title: "Retail & Supermarkets",
    image: IMG.market,
    alt: "Fresh produce display in a premium grocery store",
    text: "Shelf life is shelf value. Extending the display window of fresh produce can reduce markdowns, shrink and the daily disposal bill.",
  },
];

const fmt = (n) => new Intl.NumberFormat("en-US").format(Math.round(n));

export default function ForBusiness() {
  const [sector, setSector] = useState(SECTORS[0]);
  const [spend, setSpend] = useState(250000);
  const [waste, setWaste] = useState(18);

  const monthlySavings = spend * (waste / 100) * 0.4;
  const annualSavings = monthlySavings * 12;

  return (
    <div data-testid="for-business-page">
      <PageHero
        chapter="05"
        tag="For Business"
        testId="business-hero"
        lines={[<>Fresh produce,</>, <>ready when you are.</>]}
        lead="Fresh Guard is building pilot partnerships with hotels, restaurants, caterers and retailers — with the goal of turning longer shelf life into measurable margin."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24 sm:pb-32">
        <Reveal><Tag>Built for your sector</Tag></Reveal>
        <div className="mt-10 flex flex-wrap gap-2">
          {SECTORS.map((s) => (
            <button
              key={s.id}
              data-testid={`sector-tab-${s.id}`}
              onClick={() => setSector(s)}
              className={`inline-flex items-center gap-2 rounded-full border px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
                sector.id === s.id
                  ? "bg-forest text-cream border-forest"
                  : "bg-white border-forest/15 text-moss hover:border-forest/50"
              }`}
            >
              <s.icon className="h-4 w-4" />
              {s.title}
            </button>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={sector.id}
            data-testid="sector-panel"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
            className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center border border-forest/10 bg-white p-6 sm:p-10"
          >
            <div className="relative overflow-hidden aspect-[4/3]">
              <img src={sector.image} alt={sector.alt} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            </div>
            <div>
              <h3 className="font-display font-extrabold text-2xl sm:text-3xl text-forest">{sector.title}</h3>
              <p className="mt-4 text-base leading-relaxed text-moss">{sector.text}</p>
              <Link
                to="/contact"
                data-testid="sector-cta-pilot"
                className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-emerald hover:text-forest transition-colors"
              >
                Request a pilot
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        </AnimatePresence>
      </section>

      <section className="bg-white border-y border-forest/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 sm:py-32">
          <Reveal><Tag>The business case</Tag></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display font-extrabold tracking-tight leading-[1.1] text-3xl sm:text-4xl text-forest max-w-2xl">
              What could longer freshness be worth to you?
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-12 border border-forest/10 bg-cream p-8 sm:p-12" data-testid="roi-calculator">
              <div className="space-y-10">
                <div>
                  <div className="flex justify-between items-baseline mb-3">
                    <label htmlFor="roi-spend" className="font-mono text-[10px] uppercase tracking-[0.2em] text-fog">
                      Monthly produce spend
                    </label>
                    <span className="font-mono text-sm text-forest">SAR {fmt(spend)}</span>
                  </div>
                  <input
                    id="roi-spend"
                    data-testid="roi-spend-slider"
                    type="range"
                    min="10000"
                    max="2000000"
                    step="10000"
                    value={spend}
                    onChange={(e) => setSpend(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <div className="flex justify-between items-baseline mb-3">
                    <label htmlFor="roi-waste" className="font-mono text-[10px] uppercase tracking-[0.2em] text-fog">
                      Current produce waste rate
                    </label>
                    <span className="font-mono text-sm text-forest">{waste}%</span>
                  </div>
                  <input
                    id="roi-waste"
                    data-testid="roi-waste-slider"
                    type="range"
                    min="5"
                    max="40"
                    step="1"
                    value={waste}
                    onChange={(e) => setWaste(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-fog leading-relaxed">
                  Illustrative model only — assumes Fresh Guard achieves its target of reducing
                  produce waste by up to 40%. Not a measured result; actual outcomes vary by
                  operation.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-6 content-center">
                <div className="border-l-2 border-emerald pl-6" data-testid="roi-monthly-savings">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fog">Potential monthly savings</p>
                  <p className="mt-2 font-mono text-4xl sm:text-5xl text-forest">SAR {fmt(monthlySavings)}</p>
                </div>
                <div className="border-l-2 border-emerald pl-6" data-testid="roi-annual-savings">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fog">Potential annual savings</p>
                  <p className="mt-2 font-mono text-4xl sm:text-5xl text-forest">SAR {fmt(annualSavings)}</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 sm:py-32">
        <Reveal><Tag>How partnership works</Tag></Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { n: "01", title: "Pilot", text: "We run a scoped pilot in your operation — one category, one site, clear metrics." },
            { n: "02", title: "Measure", text: "Shelf life, waste rate and margin impact are tracked together, transparently." },
            { n: "03", title: "Scale", text: "Results from validated pilots guide the rollout across sites, categories and regions." },
          ].map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1}>
              <div data-testid={`partnership-step-${s.n}`} className="border border-forest/10 bg-white p-8 h-full">
                <span className="font-mono text-xs text-amber">{s.n}</span>
                <h3 className="mt-4 font-display font-bold text-2xl text-forest">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-moss">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="text-center mt-16">
          <Link
            to="/contact"
            data-testid="business-cta-contact"
            className="inline-flex items-center gap-2 rounded-full bg-forest text-cream px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-emerald"
          >
            Start a pilot conversation
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
